import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/heatmap"; // registers automatically

export default function HeatmapChart({ tickets }) {
  const data = useMemo(() => {
    const filtered = tickets.filter((s) => s.was_scanned && s.scanned_in_time);
    if (filtered.length === 0) return { points: [], sections: [], minutes: [] };

    // Extract unique sections and sort them numerically if possible
    const sections = [...new Set(filtered.map((s) => s.section_number))].sort(
      (a, b) => {
        return parseInt(a) - parseInt(b) || a.localeCompare(b);
      },
    );

    const sectionMap = new Map();
    sections.forEach((sec, i) => sectionMap.set(sec, i));

    // Create minute buckets (from first scan - 30 min to last scan + 30 min)
    const times = filtered.map((s) => new Date(s.scanned_in_time));
    const minTime = new Date(
      Math.min(...times.map((t) => t.getTime())) - 30 * 60 * 1000,
    );
    const maxTime = new Date(
      Math.max(...times.map((t) => t.getTime())) + 30 * 60 * 1000,
    );

    const points = []; // [minuteIndex, sectionIndex, count]

    filtered.forEach((scan) => {
      const t = new Date(scan.scanned_in_time).getTime();
      const minuteIndex = Math.floor((t - minTime.getTime()) / 60000); // per minute
      const sectionIndex = sectionMap.get(scan.section_number);

      // Find existing or push new
      const existing = points.find(
        (p) => p[0] === minuteIndex && p[1] === sectionIndex,
      );
      if (existing) {
        existing[2] += 1;
      } else {
        points.push([minuteIndex, sectionIndex, 1]);
      }
    });

    return {
      points,
      sections,
      startTime: minTime.getTime(),
    };
  }, [tickets]);

  const options = {
    chart: { type: "heatmap", inverted: false, height: 600 },
    title: {
      text: "Ticket Scan Heatmap – By Section & Time",
      style: { color: "#fff" },
    },
    subtitle: {
      text: "Darker = more scans in that minute",
      style: { color: "#aaa" },
    },
    colorAxis: {
      min: 0,
      stops: [
        [0, "#1f2937"],
        [0.2, "#1e40af"],
        [0.5, "#7c3aed"],
        [0.8, "#ec4899"],
        [1, "#f59e0b"],
      ],
      labels: { style: { color: "#fff" } },
    },
    xAxis: {
      type: "datetime",
      labels: {
        format: "{value:%H:%M}",
        style: { color: "#ccc" },
      },
      min: data.startTime,
      max:
        data.startTime +
        (data.points.length > 0
          ? Math.max(...data.points.map((p) => p[0])) * 60000 + 60 * 60000
          : 60 * 60000),
      title: { text: "Time of Day", style: { color: "#fff" } },
    },
    yAxis: {
      categories: data.sections,
      title: { text: "Section", style: { color: "#fff" } },
      labels: { style: { color: "#fff" } },
    },
    tooltip: {
      formatter: function () {
        const time = Highcharts.dateFormat("%H:%M", this.point.x);
        return `<b>${this.point.value} scan${this.point.value === 1 ? "" : "s"}</b><br/>
                Section <b>${data.sections[this.point.y]}</b><br/>
                at <b>${time}</b>`;
      },
    },
    series: [
      {
        type: "heatmap",
        name: "Scans per minute",
        borderWidth: 1,
        borderColor: "#333",
        data: data.points,
        turboThreshold: 0,
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div style={{ padding: 0, borderRadius: 12 }}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
}
